import React, { useState } from 'react';
import FormInput from '../FormInput';
import PageTitle from '../PageTitle';
import TextInput from '../textInput/TextInput';
import { RoutePaths, EmailUpdateStatus } from 'Constants/Common';
import { useNavigate } from 'react-router-dom';
import {
    CustomerPolicies,
    EmailUpdateRequest,
    VehicleType
} from '../CommonTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { EmailUpdateApi } from 'api/EmailUpdateApi';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { setEmail as setEmailAction } from 'state/slices/customerSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';

type PageErrors = {
    emailAddressError: string | undefined;
};

const AmendEmailAddressPage = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const customerEmail = useAppSelector((state) => state.customer.email);
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const { getAccessTokenSilently, user, logout } = useAuth0();

    const getPolicyForEmailUpdate = (v: VehicleType): CustomerPolicies => ({
        customerEmail: customerEmail ?? '',
        vehicleRegNumber: v.vrn,
        productRefNumbers: v.policies.map((p) => p.reference)
    });

    const navigate = useNavigate();
    const [email, setEmail] = useState(customerEmail);
    const [pageErrors, setPageErrors] = useState<PageErrors>();
    const updateEmail = () => {
        const sendEditNameEmail = async () => {
            const controller = new AbortController();
            const emailUpdatedinVscMessage =
                'Your email address has been successfully updated. Please be aware you will need to sign up with your new email address when logging into the portal.';
            const emailUpdatedinAuth0Message = t(
                'email_submit_toast_successmsg'
            );

            const updateEmailSuccess = (response: number) => {
                if (
                    response == EmailUpdateStatus.UpdatedInAuth0 ||
                    response == EmailUpdateStatus.UpdatedInVsc
                ) {
                    const emailSuccessMessage =
                        response == EmailUpdateStatus.UpdatedInVsc
                            ? emailUpdatedinVscMessage
                            : emailUpdatedinAuth0Message;

                    dispatch(setEmailAction(email));
                    toast.success(emailSuccessMessage, {
                        onClose: () => {
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin
                                }
                            });
                        }
                    });
                } else {
                    showErrorMessage();
                }
            };

            const updateEmailFailure = () => {
                showErrorMessage();
            };
            const showErrorMessage = () => {
                toast.error(t('email_submit_toast_errormsg'));
            };

            const token = await getAccessTokenSilently();

            const customerPolicies = vehicles.map(getPolicyForEmailUpdate);

            const contactDetails = {
                newEmail: email,
                homephone: '',
                workPhone: '',
                mobilePhone: ''
            };

            const data: EmailUpdateRequest = {
                userId: user.sub,
                policies: customerPolicies,
                contactDetails: contactDetails
            };

            new EmailUpdateApi().UpdateEmail(
                data,
                token,
                updateEmailSuccess,
                updateEmailFailure,
                controller.signal,
                { skipToastNotification: true }
            );

            return () => {
                controller.abort();
            };
        };

        sendEditNameEmail();
    };

    const onCancelClicked = () => {
        navigate(RoutePaths.PersonalDetails);
    };

    const onSubmit = () => {
        if (validateInputs()) {
            updateEmail();
        }
    };

    const validateInputs = (): boolean => {
        const validationErrors: PageErrors = {
            emailAddressError: undefined
        };
        if (email === '') {
            validationErrors.emailAddressError = t('email_address_is_required');
        } else if (email.length > 50) {
            validationErrors.emailAddressError = t(
                'invalid_email_too_many_characters'
            );
        } else {
            if (!isValidEmail(email)) {
                validationErrors.emailAddressError = t(
                    'email_address_is_invalid'
                );
            } else {
                validationErrors.emailAddressError = null;
            }
        }
        setPageErrors(validationErrors);

        const isPageValid = Object.values(validationErrors).every(
            (el) => el === undefined || el === null
        );

        return isPageValid;
    };

    const getErrors = () => {
        const errors = [];

        if (pageErrors) {
            pageErrors.emailAddressError &&
                errors.push(pageErrors.emailAddressError);
        }

        return errors;
    };

    const isValidEmail = (email) => {
        const regexp = new RegExp(
            /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
        return regexp.test(email);
    };

    const updateEmailAddress = (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => {
        const value = event.currentTarget.value;

        switch (key) {
            case 'emailAddress':
                setEmail(value);
                break;
            default:
                break;
        }
    };
    return (
        <div className='card page-content'>
            <PageTitle title={t('amend_email_title')} />
            <FormInput
                id='amendEmailAddress'
                label={t('emaildetails_label')}
                errorText={pageErrors?.emailAddressError}>
                <div className='row'>
                    <div>
                        <TextInput
                            name='emailAddress'
                            value={email || ''}
                            onChange={(e) =>
                                updateEmailAddress(e, 'emailAddress')
                            }
                        />
                    </div>
                </div>
            </FormInput>
            <div style={{ padding: '5px' }}>
                <small>
                    <i>{t('email_update_info')}</i>
                </small>
            </div>
            <div className='bottom-navigation'>
                <button
                    name='btnSubmit'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onSubmit}>
                    {t('submit')}
                </button>
                <button
                    name='btnCancel'
                    data-testid='secondary-button'
                    className='btn btn-secondary progress-button'
                    onClick={onCancelClicked}>
                    {t('cancel')}
                </button>
            </div>
            <div className='card-footer page-content-footer'>
                <ErrorSummary
                    title={t('page_errors')}
                    errors={getErrors()}
                    className='card-error'
                />
            </div>
        </div>
    );
};

export default AmendEmailAddressPage;
